import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PatientIndicatorsPDFWrapper } from "@/components/patient/PatientIndicatorsPDFWrapper";
import { SubcategoryChart } from "@/components/patient/SubcategoryChart";
import { Period } from "@/components/patient/PatientHealthIndicators";
import { Skeleton } from "@/components/ui/skeleton";
import { useExportToPDF } from "@/hooks/useExportToPDF";

interface Subcategory {
  id: string;
  name: string;
  category_id: string;
  category_name: string;
}

interface LabResultWithSubcategory {
  id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string | null;
  created_at: string;
  subcategory_id: string | null;
  subcategory_name: string | null;
  category_name: string | null;
}

export default function PacienteRelatorioPDF() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const period = (searchParams.get('periodo') || 'all') as Period;

  const [patient, setPatient] = useState<any>(null);
  const [professional, setProfessional] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [subcategoriesWithData, setSubcategoriesWithData] = useState<Subcategory[]>([]);
  const [groupedResults, setGroupedResults] = useState<Record<string, LabResultWithSubcategory[]>>({});
  const [loading, setLoading] = useState(true);

  const pdfContentRef = useRef<HTMLDivElement>(null);
  const { exportToPDF, isExporting } = useExportToPDF(
    pdfContentRef,
    patient
      ? `relatorio-completo-${patient.full_name}-${new Date().toISOString().split('T')[0]}.pdf`
      : "relatorio-completo.pdf"
  );

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      // Buscar paciente com validação de ownership
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Usuário não autenticado");
        navigate("/app/pacientes");
        return;
      }

      const { data: patientData, error: patientError } = await supabase
        .from("patients_onboarding")
        .select("*")
        .eq("id", id)
        .eq("owner_id", user.id)
        .maybeSingle();

      if (patientError || !patientData) {
        toast.error("Paciente não encontrado ou sem permissão");
        navigate("/app/pacientes");
        return;
      }
      setPatient(patientData);

      // Buscar profissional
      const { data: professionalData } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      setProfessional(professionalData);

      // Buscar subcategorias com dados
      const { data: subcategoriesData } = await supabase
        .from("lab_subcategories")
        .select(`
          id,
          name,
          category_id,
          lab_categories!inner(name)
        `)
        .eq("user_id", user.id)
        .eq("show_chart", true)
        .order("name");

      const formattedSubcategories = (subcategoriesData || []).map(sub => ({
        id: sub.id,
        name: sub.name,
        category_id: sub.category_id,
        category_name: (sub.lab_categories as any)?.name || 'Sem Categoria'
      }));

      // Buscar resultados
      const { data: resultsData } = await supabase
        .from("lab_results_onboarding")
        .select(`
          id,
          test_name,
          value,
          unit,
          ref_low,
          ref_high,
          collected_at,
          created_at,
          subcategory_id,
          lab_subcategories(
            id,
            name,
            lab_categories(name)
          )
        `)
        .eq("patient_id", patientData.id)
        .not("subcategory_id", "is", null)
        .order("collected_at", { ascending: false });

      const formattedResults: LabResultWithSubcategory[] = (resultsData || []).map(result => ({
        id: result.id,
        test_name: result.test_name,
        value: result.value,
        unit: result.unit,
        ref_low: result.ref_low,
        ref_high: result.ref_high,
        collected_at: result.collected_at,
        created_at: result.created_at,
        subcategory_id: result.subcategory_id,
        subcategory_name: (result.lab_subcategories as any)?.name,
        category_name: (result.lab_subcategories as any)?.lab_categories?.name
      }));

      // Agrupar por subcategoria
      const grouped = formattedResults.reduce((acc, result) => {
        if (result.subcategory_id) {
          if (!acc[result.subcategory_id]) {
            acc[result.subcategory_id] = [];
          }
          acc[result.subcategory_id].push(result);
        }
        return acc;
      }, {} as Record<string, LabResultWithSubcategory[]>);

      // Filtrar subcategorias que têm dados
      const subsWithData = formattedSubcategories.filter(sub =>
        grouped[sub.id] && grouped[sub.id].length > 0
      );

      setSubcategoriesWithData(subsWithData);
      setGroupedResults(grouped);

      // Calcular estatísticas
      const allResults = Object.values(grouped).flat();
      const totalMeasurements = allResults.length;
      const outOfRange = allResults.filter(r => {
        if (r.value === null) return false;
        if (r.ref_low !== null && r.value < r.ref_low) return true;
        if (r.ref_high !== null && r.value > r.ref_high) return true;
        return false;
      }).length;
      const normalRange = allResults.filter(r => {
        if (r.value === null) return false;
        if (r.ref_low !== null && r.value < r.ref_low) return false;
        if (r.ref_high !== null && r.value > r.ref_high) return false;
        return true;
      }).length;

      setStats({
        totalIndicators: subsWithData.length,
        totalMeasurements,
        outOfRange,
        normalRange
      });

      console.log("📊 Dados do relatório prontos:", {
        patientId: patientData.id,
        hasPatient: !!patientData,
        subcategoriesWithData: subsWithData.length,
        totalMeasurements,
        outOfRange,
        normalRange,
      });

      setLoading(false);

      console.log("[PDF] Preparando exportação automática...", {
        hasRef: !!pdfContentRef.current,
      });

      if (pdfContentRef.current) {
        console.log("[PDF] Dimensões do container antes do timeout:", {
          offsetWidth: pdfContentRef.current.offsetWidth,
          offsetHeight: pdfContentRef.current.offsetHeight,
          scrollWidth: pdfContentRef.current.scrollWidth,
          scrollHeight: pdfContentRef.current.scrollHeight,
          svgCount: pdfContentRef.current.querySelectorAll("svg").length,
          pdfCharts: pdfContentRef.current.querySelectorAll(".pdf-chart").length,
        });
      }

      // Disparar exportação PDF após carregar e renderizar
      setTimeout(async () => {
        if (pdfContentRef.current) {
          console.log("[PDF] Dimensões do container na hora da exportação:", {
            offsetWidth: pdfContentRef.current.offsetWidth,
            offsetHeight: pdfContentRef.current.offsetHeight,
            scrollWidth: pdfContentRef.current.scrollWidth,
            scrollHeight: pdfContentRef.current.scrollHeight,
            svgCount: pdfContentRef.current.querySelectorAll("svg").length,
            pdfCharts: pdfContentRef.current.querySelectorAll(".pdf-chart").length,
          });
        } else {
          console.warn("[PDF] pdfContentRef.current está null na hora da exportação!");
        }

        console.log("📄 Gerando PDF automaticamente...");
        await exportToPDF();
        console.log("✅ PDF gerado com sucesso!");

        // Redirecionar de volta após 1s
        setTimeout(() => {
          navigate(`/app/pacientes/${id}`);
        }, 1000);
      }, 2000); // 2s para garantir renderização completa dos gráficos Recharts
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Adicionar estilos específicos para impressão
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        body { margin: 0; padding: 20px; }
        @page { size: A4; margin: 15mm; }
      }
      @media screen {
        body { background: #f5f5f5; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (loading || isExporting) {
    return (
      <div className="p-8 max-w-5xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">
              {loading ? "Carregando dados do relatório..." : "Gerando PDF..."}
            </h2>
            <p className="text-muted-foreground">
              {loading
                ? "Por favor, aguarde enquanto preparamos seu relatório"
                : "Seu download iniciará em instantes"
              }
            </p>
          </div>
          <div className="space-y-4">
            <Skeleton className="h-8 w-64 mx-auto" />
            <Skeleton className="h-4 w-48 mx-auto" />
          </div>
          <div className="grid grid-cols-4 gap-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!patient || !professional || !stats) {
    return (
      <div className="p-8 max-w-5xl mx-auto text-center">
        <p className="text-muted-foreground">Erro ao carregar dados do relatório.</p>
      </div>
    );
  }

  return (
    <div
      ref={pdfContentRef}
      className="bg-white"
      style={{ width: '210mm', margin: '0 auto', padding: '20px' }}
    >
      <PatientIndicatorsPDFWrapper
        patient={patient}
        professional={professional}
        stats={stats}
      >
        <div className="space-y-8">
          {subcategoriesWithData.map((subcategory) => (
            <div key={subcategory.id} className="pdf-chart">
              <SubcategoryChart
                subcategory={subcategory}
                results={groupedResults[subcategory.id] || []}
                period={period}
                pdfMode={true}
              />
            </div>
          ))}
        </div>
      </PatientIndicatorsPDFWrapper>
    </div>
  );
}
